import { useEffect, useState } from "react";
import { useGetVariantById, useUpdateVariant } from "../../../api/recipeSimulator";
import { Button } from "../../../components/ui/common/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "../../../components/ui/common/dialog";
import { Input } from "../../../components/ui/common/input";
import { Label } from "../../../components/ui/common/label";
import { Textarea } from "../../../components/ui/common/textarea";
import { FOCUS_RING_CLASS } from "../../../config/constants";

const VARIANT_NAME_MAX_LENGTH = 15;

interface UpdateRecipeVariantModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  recipeId: string;
  variantId: string | null;
  recipeDataRefetch: () => void;
}

const UpdateRecipeVariantModal = ({
  open,
  onOpenChange,
  recipeId,
  variantId,
  recipeDataRefetch
}: UpdateRecipeVariantModalProps) => {
  const [name, setName] = useState("");
  const [notes, setNotes] = useState("");
  const { data: variantData, isFetching: isLoading } = useGetVariantById(
    recipeId,
    variantId ?? ""
  );
  const { mutate: updateVariant, isLoading: isUpdating } = useUpdateVariant();

  useEffect(() => {
    if (open && variantData) {
      setName(variantData.variantName ?? "");
      setNotes(variantData.notes ?? "");
    }
  }, [open, variantData]);

  const isBusy = isLoading || isUpdating;
  const trimmedName = name.trim();
  const canSubmit = Boolean(trimmedName) && Boolean(variantData) && !isBusy;

  const handleSubmit = () => {
    if (!canSubmit || !variantData || !variantId) {
      return;
    }

    updateVariant(
      {
        recipeId,
        variantId,
        variant: {
          ...variantData,
          variantName: trimmedName,
          notes: notes.trim()
        }
      },
      {
        onSuccess: async () => {
          await recipeDataRefetch();
        },
        onSettled: () => {
          onOpenChange(false);
        }
      }
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="tw-max-w-md">
        <DialogHeader>
          <DialogTitle>Edit Variation</DialogTitle>
          <DialogDescription>
            Give this variation a name and capture any notes that describe how it differs from the
            baseline.
          </DialogDescription>
        </DialogHeader>
        <div className="tw-space-y-4 tw-py-2">
          <div className="tw-space-y-1.5">
            <Label htmlFor="edit-variation-name">Variation name</Label>
            <Input
              id="edit-variation-name"
              value={name}
              onChange={event => setName(event.target.value)}
              placeholder="e.g. Low Fat"
              maxLength={VARIANT_NAME_MAX_LENGTH}
              className={FOCUS_RING_CLASS}
              autoFocus
              disabled={isBusy}
            />
          </div>
          <div className="tw-space-y-1.5">
            <Label htmlFor="edit-variation-notes">Notes</Label>
            <Textarea
              id="edit-variation-notes"
              value={notes}
              onChange={event => setNotes(event.target.value)}
              placeholder="What changes in this variation and why?"
              className={FOCUS_RING_CLASS}
              rows={4}
              disabled={isBusy}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isBusy}>
            Cancel
          </Button>
          <Button disabled={!canSubmit} onClick={handleSubmit}>
            {isUpdating ? "Saving..." : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default UpdateRecipeVariantModal;
